import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { isProduction } from '../constants/environment';

interface DevSettingsState {
  showEnvBanner: boolean;
  setShowEnvBanner: (show: boolean) => void;
  showEnvFavicon: boolean;
  setShowEnvFavicon: (show: boolean) => void;
  showSlotAvailability: boolean;
  setShowSlotAvailability: (show: boolean) => void;
  enableDetailedChatData: boolean;
  setEnableDetailedChatData: (enabled: boolean) => void;
  // When on, every tracking signal (event/measurement/identify/register) is
  // console.info'd with its destinations (Grafana/Faro, Mixpanel). Debug aid.
  logTrackingEvents: boolean;
  setLogTrackingEvents: (enabled: boolean) => void;
  // Runtime override of VITE_APP_BE_URL. Lets non-production builds (e.g. Netlify
  // deploy previews) route at an arbitrary backend such as a local API exposed via
  // a Tailscale funnel. null = use the build-time VITE_APP_BE_URL.
  apiBaseUrlOverride: string | null;
  setApiBaseUrlOverride: (url: string | null) => void;
}

const useDevSettingsStore = create<DevSettingsState>()(
  devtools(
    persist(
      (set) => ({
        showEnvBanner: true,
        setShowEnvBanner: (show) => set({ showEnvBanner: show }),
        showEnvFavicon: true,
        setShowEnvFavicon: (show) => set({ showEnvFavicon: show }),
        showSlotAvailability: false,
        setShowSlotAvailability: (show) => set({ showSlotAvailability: show }),
        enableDetailedChatData: true,
        setEnableDetailedChatData: (enabled) => set({ enableDetailedChatData: enabled }),
        logTrackingEvents: false,
        setLogTrackingEvents: (enabled) => set({ logTrackingEvents: enabled }),
        apiBaseUrlOverride: null,
        setApiBaseUrlOverride: (url) => set({ apiBaseUrlOverride: url })
      }),
      {
        name: 'dev-settings',
        partialize: (state) => ({
          showEnvBanner: state.showEnvBanner,
          showEnvFavicon: state.showEnvFavicon,
          showSlotAvailability: state.showSlotAvailability,
          enableDetailedChatData: state.enableDetailedChatData,
          logTrackingEvents: state.logTrackingEvents,
          apiBaseUrlOverride: state.apiBaseUrlOverride
        })
      }
    )
  )
);

export const useShowEnvBanner = () => useDevSettingsStore((state) => state.showEnvBanner);
export const useSetShowEnvBanner = () => useDevSettingsStore((state) => state.setShowEnvBanner);
export const useShowEnvFavicon = () => useDevSettingsStore((state) => state.showEnvFavicon);
export const useSetShowEnvFavicon = () => useDevSettingsStore((state) => state.setShowEnvFavicon);
export const useShowSlotAvailability = () => useDevSettingsStore((state) => state.showSlotAvailability);
export const useSetShowSlotAvailability = () => useDevSettingsStore((state) => state.setShowSlotAvailability);
export const useEnableDetailedChatData = () => useDevSettingsStore((state) => state.enableDetailedChatData);
export const useSetEnableDetailedChatData = () => useDevSettingsStore((state) => state.setEnableDetailedChatData);
export const useLogTrackingEvents = () => useDevSettingsStore((state) => state.logTrackingEvents);
export const useSetLogTrackingEvents = () => useDevSettingsStore((state) => state.setLogTrackingEvents);
export const useApiBaseUrlOverride = () => useDevSettingsStore((state) => state.apiBaseUrlOverride);
export const useSetApiBaseUrlOverride = () => useDevSettingsStore((state) => state.setApiBaseUrlOverride);

// Non-hook accessor for use outside React (app bootstrap in index.tsx). The persist
// middleware hydrates from localStorage synchronously on import, so getState() returns
// the persisted value at startup.
const getApiBaseUrlOverride = () => useDevSettingsStore.getState().apiBaseUrlOverride;

// Non-hook accessor for the tracking dispatcher's `onEvent` hook, which runs
// outside React. Reads live so toggling takes effect without a re-configure.
export const getLogTrackingEvents = () => useDevSettingsStore.getState().logTrackingEvents;

// Resolves the backend base URL the app should boot with. The override is honored only
// in non-production builds, so a production bundle can never be pointed at another API.
export const resolveApiBaseUrl = (buildTimeUrl: string): string => {
  if (isProduction) {
    return buildTimeUrl;
  }
  return getApiBaseUrlOverride() || buildTimeUrl;
};
